import React, { useState } from 'react';
import {
    Box,
    Typography,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Select,
    MenuItem,
    Slider,
    Switch,
    Button,
    CircularProgress,
    Chip,
    FormControl,
    InputLabel,
    TextField,
} from '@mui/material';
import {
    ExpandMore as ExpandMoreIcon,
    Send as SendIcon,
    DeviceHub as DeviceIcon,
} from '@mui/icons-material';
import type { SenzorCapabilities, WritableFeature } from '../../api/JsdhNotifikaceService/generated';
import { parseWritableFeatures } from '../../utils/sensorCapabilities';
import { useSendFeatureCommandMutation } from '../../api/JsdhNotifikaceService/service/IotMutations';

// =====================
// FEATURE CONTROL COMPONENTS
// =====================

interface FeatureControlProps {
    feature: WritableFeature;
    senzorId: number;
}

const EnumFeatureControl: React.FC<FeatureControlProps> = ({ feature, senzorId }) => {
    const [value, setValue] = useState<string>(feature.values?.[0] || '');
    const sendCommand = useSendFeatureCommandMutation();

    const handleSend = () => {
        sendCommand.mutate({
            senzorId,
            data: { property: feature.property, value },
        });
    };

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>{feature.property}</InputLabel>
                <Select
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    label={feature.property}
                >
                    {feature.values?.map((v) => (
                        <MenuItem key={v} value={v}>{v}</MenuItem>
                    ))}
                </Select>
            </FormControl>
            <Button
                size="small"
                variant="outlined"
                onClick={handleSend}
                disabled={sendCommand.isPending}
                startIcon={sendCommand.isPending ? <CircularProgress size={16} /> : <SendIcon />}
            >
                Odeslat
            </Button>
        </Box>
    );
};

const NumericFeatureControl: React.FC<FeatureControlProps> = ({ feature, senzorId }) => {
    const [value, setValue] = useState<number>(feature.min ?? 0);
    const sendCommand = useSendFeatureCommandMutation();

    const handleSend = () => {
        sendCommand.mutate({
            senzorId,
            data: { property: feature.property, value },
        });
    };

    const hasRange = feature.min != null && feature.max != null;

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 250 }}>
            <Typography variant="body2" sx={{ minWidth: 80 }}>
                {feature.property}
            </Typography>
            {hasRange ? (
                <Slider
                    value={value}
                    onChange={(_, v) => setValue(v as number)}
                    min={feature.min}
                    max={feature.max}
                    step={feature.step ?? 1}
                    valueLabelDisplay="auto"
                    valueLabelFormat={(v) => feature.unit ? `${v} ${feature.unit}` : `${v}`}
                    sx={{ flex: 1 }}
                />
            ) : (
                <TextField
                    type="number"
                    size="small"
                    value={value}
                    onChange={(e) => setValue(Number(e.target.value))}
                    inputProps={{ min: feature.min, max: feature.max, step: feature.step }}
                    sx={{ width: 100 }}
                />
            )}
            {feature.unit && (
                <Typography variant="caption" color="text.secondary">
                    {feature.unit}
                </Typography>
            )}
            <Button
                size="small"
                variant="outlined"
                onClick={handleSend}
                disabled={sendCommand.isPending}
                startIcon={sendCommand.isPending ? <CircularProgress size={16} /> : <SendIcon />}
            >
                Odeslat
            </Button>
        </Box>
    );
};

const BinaryFeatureControl: React.FC<FeatureControlProps> = ({ feature, senzorId }) => {
    const [checked, setChecked] = useState(false);
    const sendCommand = useSendFeatureCommandMutation();

    const handleToggle = (newChecked: boolean) => {
        setChecked(newChecked);
        const value = feature.valueOn && feature.valueOff
            ? (newChecked ? feature.valueOn : feature.valueOff)
            : newChecked;
        sendCommand.mutate({
            senzorId,
            data: { property: feature.property, value },
        });
    };

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ minWidth: 80 }}>
                {feature.property}
            </Typography>
            <Switch
                checked={checked}
                onChange={(_, c) => handleToggle(c)}
                disabled={sendCommand.isPending}
                size="small"
            />
            {sendCommand.isPending && <CircularProgress size={16} />}
        </Box>
    );
};

const CompositeFeatureControl: React.FC<FeatureControlProps> = ({ feature, senzorId }) => {
    if (!feature.features?.length) return null;

    return (
        <Box sx={{ pl: 2, borderLeft: '2px solid', borderColor: 'divider' }}>
            <Typography variant="body2" fontWeight="bold" sx={{ mb: 1 }}>
                {feature.property}
            </Typography>
            {feature.features.map((subFeature) => (
                <Box key={subFeature.property} sx={{ mb: 1 }}>
                    <FeatureControl feature={subFeature} senzorId={senzorId} />
                </Box>
            ))}
        </Box>
    );
};

const FeatureControl: React.FC<FeatureControlProps> = ({ feature, senzorId }) => {
    switch (feature.type) {
        case 'enum':
            return <EnumFeatureControl feature={feature} senzorId={senzorId} />;
        case 'numeric':
            return <NumericFeatureControl feature={feature} senzorId={senzorId} />;
        case 'binary':
            return <BinaryFeatureControl feature={feature} senzorId={senzorId} />;
        case 'composite':
            return <CompositeFeatureControl feature={feature} senzorId={senzorId} />;
        default:
            return null;
    }
};

// Filtrovane features — vynechat warning/alarm (ty maji dedikovanou sirenu)
const HIDDEN_FEATURES = new Set(['warning', 'alarm']);

// =====================
// MAIN COMPONENT
// =====================

interface DeviceCapabilitiesPanelProps {
    capabilities: SenzorCapabilities;
}

const DeviceCapabilitiesPanel: React.FC<DeviceCapabilitiesPanelProps> = ({ capabilities }) => {
    const features = parseWritableFeatures(capabilities)
        .filter(f => !HIDDEN_FEATURES.has(f.property));

    if (features.length === 0 && !capabilities.model) {
        return null;
    }

    return (
        <Accordion
            disableGutters
            sx={{ mt: 1, '&:before': { display: 'none' } }}
        >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <DeviceIcon fontSize="small" color="action" />
                    <Typography variant="body2">
                        Nastavení zařízení
                    </Typography>
                    {capabilities.model && (
                        <Chip
                            label={capabilities.model}
                            size="small"
                            variant="outlined"
                        />
                    )}
                </Box>
            </AccordionSummary>
            <AccordionDetails>
                {capabilities.manufacturer && capabilities.model && (
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                        {capabilities.manufacturer} {capabilities.model}
                    </Typography>
                )}

                {features.length > 0 ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        {features.map((feature) => (
                            <FeatureControl
                                key={feature.property}
                                feature={feature}
                                senzorId={capabilities.senzorId}
                            />
                        ))}
                    </Box>
                ) : (
                    <Typography variant="body2" color="text.secondary">
                        Žádné ovladatelné vlastnosti.
                    </Typography>
                )}

                {capabilities.updatedAt && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                        Vlastnosti načteny: {new Date(capabilities.updatedAt).toLocaleString('cs-CZ')}
                    </Typography>
                )}
            </AccordionDetails>
        </Accordion>
    );
};

export default DeviceCapabilitiesPanel;
